/**
 * №29 (D-L1) — THE RIGHT ZONE: the persistent WORK RAIL.
 *
 * "RIGHT: the WORK RAIL — the crew's working state, persistent across subjects,
 * tabbed **Plan · Context · Receipts · Memory** (Chat joins the tabs when the
 * subject is not chat — mockup B)."
 *
 * ## What this component is
 *
 * The rail is App-Shell-level: mounted ONCE, a grid sibling of the stage, and it
 * survives every navigation (D-SS2's re-housed D-CH3 — the shell gate asserts its
 * mount identity across a route sequence). This file is that mount, plus the tab
 * strip, plus the collapse behaviour, plus the four working-state tabs.
 *
 * ## S1 — the tabs are filled, from records
 *
 * S0 shipped this frame with an honest notice in each tab. S1 fills all four from the
 * shipped readers — the workflow spine (Plan), the record + money + the money wall
 * (Context), run receipts (Receipts), the hire's learnings (Memory). What binds them
 * is `useRailBinding()`: ONE resolution of "what is this rail about", so the four tabs
 * can never be about different things. The probe's rule holds throughout — *every
 * chip is a real record, never decoration* — which means every tab also has a real
 * empty state, and those are as carefully written as the full ones.
 *
 * ## The conversation is not a tab's content — it is a tenant
 *
 * Chat is mounted through a PORTAL into a stable host div (`conversation-host.tsx`)
 * that the rail's Chat pane and the chat SUBJECT's stage take turns hosting. That is
 * what lets mockup A and mockup B be the same conversation rather than two: promoting
 * it to the stage re-parents one DOM node instead of re-creating a component, so an
 * in-flight run, a half-typed message and the whole message list survive the move
 * (and, as a bonus, survive collapsing the rail — which used to drop them).
 *
 * `railTabsFor` is the other half of A-vs-B: when the SUBJECT is the conversation, the
 * rail is the crew's working state and Chat is not one of its tabs.
 */
import { type ReactNode } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import { useCoPanel, type RailTabId } from "../collab/co-panel-context";
import { useConversationHost, useConversationSlot } from "../collab/conversation-host";
import { useShellIdentity } from "../../hooks/use-shell-identity";
import { CoPanel } from "../collab/CoPanel";
import { RailPlan } from "./workrail/RailPlan";
import { RailContext } from "./workrail/RailContext";
import { RailReceipts } from "./workrail/RailReceipts";
import { RailMemory } from "./workrail/RailMemory";
import { useRailBinding } from "./workrail/rail-binding";
import type { RailMode, SubjectKind } from "../../routes/app/route-anchor-matrix";
import { conversationIsSubject } from "../../routes/app/route-anchor-matrix";
import { RailStacked } from "./workrail/RailPrimitives";

/** The rail's tabs, in the probe's order. `chat` is conditional (mockup A vs B).
 *  The union itself lives on the panel provider (`RailTabId`) because the open tab
 *  is right-zone STATE — this is the same set, named locally for readability. */
type RailTab = RailTabId;

const TAB_LABEL: Record<RailTab, string> = {
  chat: "Chat",
  plan: "Plan",
  context: "Context",
  receipts: "Receipts",
  memory: "Memory",
};

/**
 * Mockup A vs B: when the SUBJECT is the conversation, the rail is the crew's
 * working state and Chat is not one of its tabs (the conversation is on stage).
 * On every other subject the conversation joins the rail as its first tab.
 */
function railTabsFor(subject: SubjectKind): RailTab[] {
  const rest: RailTab[] = ["plan", "context", "receipts", "memory"];
  return subject === "chat" ? rest : ["chat", ...rest];
}

/**
 * One section of the stacked rail. The body is rendered even when its component returns
 * nothing, and the CSS hides the whole section when it does
 * (`.railstack__sec:has(> .railstack__body:empty)`). No section had to learn whether it has
 * content — the component that already knows keeps that knowledge, and a section that
 * renders null simply is not there.
 *
 * **No title of its own.** The first build gave each section one and it read "CONTEXT" over
 * "BOUND CONTEXT" — the same word in two type sizes, because every section already labels
 * itself with a `RailHead`. The stack defers to the label the section writes, which is also
 * the more specific one.
 */
function StackSection({ testId, children }: { testId: string; children: ReactNode }) {
  return (
    <section className="railstack__sec" data-testid={`railstack-${testId}`}>
      <div className="railstack__body">{children}</div>
    </section>
  );
}

export function WorkRail({
  subject,
  mode = "work",
  conversationRoute,
}: {
  subject: SubjectKind;
  mode?: RailMode;
  /** №29 (S1) — where "open the conversation on the stage" goes, or null when this
   *  surface has no conversation row (see the matrix). Null → no control, never a
   *  disabled one. */
  conversationRoute?: string | null;
}) {
  const panel = useCoPanel();
  const navigate = useNavigate();
  const { host } = useConversationHost();
  const railSlot = useConversationSlot("rail");
  const binding = useRailBinding();
  useShellIdentity("rail");
  const tabs = railTabsFor(subject);
  // The active tab defaults to the first for this subject. Chat-on-stage means the
  // rail opens on Plan; every other subject opens on Chat.
  const active: RailTab = tabs.includes(panel.railTab) ? panel.railTab : tabs[0]!;

  // The conversation, rendered into its stable host. This portal is UNCONDITIONAL —
  // it is what guarantees one mount for the life of the shell. Which slot the host
  // is parented into is the thing that changes (see conversation-host.tsx), and a
  // collapsed rail or a two-zone surface parks it hidden rather than unmounting it.
  const conversation = host ? createPortal(<CoPanel />, host) : null;

  // The two-zone variant (mockup C): no rail on this surface. The component stays
  // MOUNTED and renders nothing visible, so the shell's identity invariant holds
  // across a three-zone → two-zone → three-zone navigation.
  if (mode === "none") {
    return (
      <>
        {conversation}
        <span className="workrail-off" data-testid="workrail-off" hidden aria-hidden="true" />
      </>
    );
  }

  // Collapsed → the edge tab (never destroyed). One full-bounding-box hit target
  // (22.3.2 F3 — the check that produced that rule still applies to this control).
  if (!panel.expanded) {
    return (
      <>
        {conversation}
        <button
          type="button"
          className="workrail-tab"
          onClick={panel.toggle}
          aria-label="Open the work rail"
          data-testid="workrail-tab"
        >
          <i className="ti ti-layout-sidebar-right-expand workrail-tab__glyph" aria-hidden="true" />
          <span className="workrail-tab__label">Work</span>
        </button>
      </>
    );
  }

  // №29 (S4.2) — ON A CONVERSATION THE RAIL IS A STACK, NOT TABS.
  //
  // Tabs are right when the rail is a reference you consult: you know which of the four
  // you want, you open it, it fills the pane. They are wrong beside a conversation, where
  // the question is never "show me Context" — it is "what does this thing know, and is
  // there anything I should see". A tab cannot answer that without being opened, so four
  // tabs is four questions the user has to ask one at a time.
  //
  // Stacked, everything that has something to say is visible at once and everything that
  // does not is gone. Which is also why this could be one flag: `RailEmpty` already had
  // every "nothing here" case routed through it.
  if (conversationIsSubject(subject)) {
    return (
      <>
        {conversation}
        <aside className="workrail workrail--stack" data-testid="workrail" aria-label="Work rail">
          <div className="workrail__tabs workrail__tabs--bare">
            <span className="workrail__stacktitle">Working state</span>
            <span className="spacer" />
            <button
              type="button"
              className="workrail__collapse"
              onClick={panel.toggle}
              aria-label="Collapse the work rail"
              title="Collapse"
              data-testid="workrail-collapse"
            >
              <i className="ti ti-layout-sidebar-right-collapse" aria-hidden="true" />
            </button>
          </div>
          <div className="workrail__body workrail__body--stack" data-testid="workrail-stack">
            <RailStacked>
              {/* Live work leads: when the conversation IS driving a run, its steps are the
                  most urgent thing in the column. When it is not, `planScope` is null and
                  this whole section is absent (S4.1) rather than showing the record's. */}
              <StackSection testId="plan"><RailPlan binding={binding} /></StackSection>
              <StackSection testId="context"><RailContext binding={binding} /></StackSection>
              <StackSection testId="memory"><RailMemory binding={binding} /></StackSection>
              <StackSection testId="receipts"><RailReceipts binding={binding} /></StackSection>
            </RailStacked>
            {/* The one line that appears when the stack is genuinely empty — otherwise a
                conversation at a bare anchor would show a titled column with nothing in it,
                which is the empty frame the honest empty states exist to prevent. */}
            <div className="railstack__none" data-testid="workrail-stack-none">
              Nothing bound to this conversation yet — context, memories and receipts appear
              here as it does things.
            </div>
          </div>
        </aside>
      </>
    );
  }

  return (
    <>
      {conversation}
      <aside className="workrail" data-testid="workrail" aria-label="Work rail">
        <div className="workrail__tabs" role="tablist" aria-label="Work rail sections">
          {tabs.map((t) => (
            <button
              key={t}
              type="button"
              role="tab"
              id={`workrail-tab-${t}`}
              aria-selected={active === t}
              aria-controls={`workrail-panel-${t}`}
              className={`workrail__tab${active === t ? " is-on" : ""}`}
              onClick={() => panel.setRailTab(t)}
              data-testid={`workrail-tab-${t}`}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
          <span className="spacer" />
          {/* №29 (S1) — which-one-is-subject is the user's choice (the probe's line).
              Shown on the Chat tab only, and only where the matrix declares a
              conversation row for this surface: a control that would land on a
              different anchor is not offered at all. */}
          {active === "chat" && conversationRoute && (
            <button
              type="button"
              className="workrail__promote"
              onClick={() => navigate(conversationRoute)}
              aria-label="Open the conversation on the stage"
              title="Open the conversation on the stage"
              data-testid="workrail-promote"
            >
              <i className="ti ti-arrow-bar-to-left" aria-hidden="true" />
            </button>
          )}
          <button
            type="button"
            className="workrail__collapse"
            onClick={panel.toggle}
            aria-label="Collapse the work rail"
            title="Collapse"
            data-testid="workrail-collapse"
          >
            <i className="ti ti-layout-sidebar-right-collapse" aria-hidden="true" />
          </button>
        </div>

        <div className="workrail__body" data-tab={active}>
          {/* The Chat pane is the conversation's SLOT, not its renderer: the host div
              is re-parented in here. It stays mounted behind the other tabs (`hidden`
              is display:none, not an unmount) so switching to Plan cannot drop a
              conversation that is mid-stream. */}
          {tabs.includes("chat") && (
            <div
              className="workrail__pane"
              role="tabpanel"
              id="workrail-panel-chat"
              aria-labelledby="workrail-tab-chat"
              hidden={active !== "chat"}
              ref={railSlot}
            />
          )}
          {active !== "chat" && (
            <div
              className="workrail__pane workrail__pane--scroll"
              role="tabpanel"
              id={`workrail-panel-${active}`}
              aria-labelledby={`workrail-tab-${active}`}
              data-testid={`workrail-pane-${active}`}
            >
              {active === "plan" && <RailPlan binding={binding} />}
              {active === "context" && <RailContext binding={binding} />}
              {active === "receipts" && <RailReceipts binding={binding} />}
              {active === "memory" && <RailMemory binding={binding} />}
            </div>
          )}
        </div>
      </aside>
    </>
  );
}
